// GameWatch — read-only spectator view at /game/watch/:code
// For parents, co-teachers, and admins who want to follow a classroom's live
// game without entering as a player. Polls the already-built /api/games/watch
// endpoint which enforces no host-privilege leakage (no answer peek before reveal).
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye, Trophy, Users, Crown, Flame, Clock, Loader2, Home, ChevronRight, Zap,
} from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { watchGame } from "@/lib/api";
import { toast } from "sonner";
import JeopardyGame from "@/pages/JeopardyGame";

// Lightweight wrapper so Jeopardy spectators get the same board/question view as players
// without the buzz-in ability (JeopardyGame enforces this when mode="watch").
const JeopardyGameWatch = ({ game }) => (
  <JeopardyGame mode="watch" initialGame={game} codeOverride={game.code} />
);

const useCountdown = (startedAt, seconds) => {
  const [remaining, setRemaining] = useState(seconds);
  useEffect(() => {
    if (!startedAt) { setRemaining(seconds); return; }
    const tick = () => {
      const started = new Date(startedAt).getTime();
      const left = Math.max(0, seconds - Math.floor((Date.now() - started) / 1000));
      setRemaining(left);
    };
    tick();
    const t = setInterval(tick, 250);
    return () => clearInterval(t);
  }, [startedAt, seconds]);
  return remaining;
};

export default function GameWatch() {
  const { code } = useParams();
  const [game, setGame] = useState(null);
  const [err, setErr] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // All hooks must run on every render (Rules of Hooks). Compute these BEFORE early returns.
  const q = game?.question || null;
  const started = q?.started_at || null;
  const remaining = useCountdown(started, q?.seconds || 20);

  // Poll every ~1.2s — same cadence as the live game so the leaderboard feels real-time.
  useEffect(() => {
    if (!code) return;
    let active = true;
    let timer = null;
    const poll = async () => {
      try {
        const g = await watchGame(code.toUpperCase());
        if (!active) return;
        setGame(g);
        setErr(null);
      } catch (e) {
        if (!active) return;
        const status = e?.response?.status;
        if (status === 404) { setNotFound(true); return; }
        setErr(e?.response?.data?.detail || "Couldn't load the game");
      }
      if (active) timer = setTimeout(poll, 1200);
    };
    poll();
    return () => { active = false; if (timer) clearTimeout(timer); };
  }, [code]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white grid place-items-center px-6 py-10">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-4xl font-black">No live game at that code</h1>
          <p className="mt-3 text-white/70">Ask the teacher to double-check the code. Watch links expire when the game ends.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-5 py-2 text-sm font-semibold hover:bg-white/20 transition">
            <Home className="h-4 w-4" /> Lectura
          </Link>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white grid place-items-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-300" />
      </div>
    );
  }

  // Spectator view for Jeopardy — delegate to JeopardyGame in watch mode so parents / co-teachers
  // see the same board + buzz state students see (but without the ability to buzz or judge).
  if (game.kind === "jeopardy") {
    return <JeopardyGameWatch game={game} />;
  }

  const players = game.players || [];
  const status = game.status;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white px-5 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Spectator banner */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2 rounded-full bg-indigo-400/20 border border-indigo-300/40 px-4 py-1.5 text-xs font-black uppercase tracking-[0.3em] text-indigo-200">
            <Eye className="h-3.5 w-3.5" /> Spectator mode · read-only
          </div>
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-[0.3em] text-white/50">Room</p>
            <p data-testid="watch-room-code" className="font-mono text-2xl font-black tracking-widest">{game.code}</p>
          </div>
        </div>

        {/* Title + status pill */}
        <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl font-black">{game.lecture_title}</h1>
            <p className="mt-1 text-sm text-white/60">
              Hosted by {game.host_name} · {players.length} player{players.length === 1 ? "" : "s"}
            </p>
          </div>
          <StatusPill status={status} q={q} />
        </header>

        {/* Current question (reveal-safe — correct_index only shown when server allows) */}
        {q && status !== "waiting" && status !== "final" && (
          <section data-testid="watch-current-question" className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6 sm:p-8 mb-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.3em] text-white/50">
                  Question {q.index + 1} of {q.total}
                </p>
                <h2 className="mt-1 font-serif text-xl sm:text-2xl font-bold">{q.question}</h2>
              </div>
              {status === "running" && (
                <div className="inline-flex items-center gap-2 rounded-full bg-amber-400/20 border border-amber-400/40 px-4 py-1.5 text-sm font-black tabular-nums text-amber-200">
                  <Clock className="h-4 w-4" /> {remaining}s
                </div>
              )}
            </div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {(q.options || []).map((opt, i) => {
                const isCorrect = q.correct_index === i;
                const showCorrect = status === "reveal" && isCorrect;
                return (
                  <div
                    key={i}
                    data-testid={`watch-option-${i}`}
                    className={`rounded-2xl border px-4 py-3 text-sm ${showCorrect
                      ? "border-emerald-400 bg-emerald-500/20 text-white font-bold"
                      : "border-white/15 bg-white/5 text-white/80"}`}
                  >
                    <span className="opacity-50 font-mono mr-2">{String.fromCharCode(65 + i)}</span>
                    {opt}
                    {showCorrect && <span className="ml-2 text-emerald-300 text-xs font-black uppercase tracking-wider">✓ Correct</span>}
                  </div>
                );
              })}
            </div>
            {status === "reveal" && q.explanation && (
              <p className="mt-4 text-sm text-white/70 italic">{q.explanation}</p>
            )}
          </section>
        )}

        {/* Leaderboard — always visible */}
        <section data-testid="watch-leaderboard" className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6 sm:p-8">
          <div className="flex items-center justify-between mb-4">
            <div className="inline-flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-300" />
              <h2 className="font-serif text-xl font-bold">Leaderboard</h2>
            </div>
            <div className="text-xs text-white/50 uppercase tracking-widest font-mono">
              {players.length} total
            </div>
          </div>
          {players.length === 0 ? (
            <p className="py-10 text-center text-white/50">
              <Users className="h-6 w-6 inline mr-2" /> Waiting for students to join…
            </p>
          ) : (
            <ul className="divide-y divide-white/10">
              <AnimatePresence>
                {players.map((p, i) => (
                  <motion.li
                    key={p.id}
                    data-testid={`watch-player-${p.id}`}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    layout
                    className={`flex items-center justify-between gap-3 py-3 ${p.is_host_player ? "bg-amber-500/10 -mx-5 px-5 rounded-xl" : ""}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`font-mono text-sm w-6 text-right ${i < 3 ? "text-amber-300 font-bold" : "text-white/40"}`}>#{i + 1}</span>
                      <Avatar id={p.avatar} size={32} streak={p.best_streak || 0} />
                      <div className="min-w-0">
                        <p className="font-semibold truncate flex items-center gap-1.5">
                          {p.name}
                          {p.is_host_player && <Crown className="h-3.5 w-3.5 text-amber-300 shrink-0" title="Teacher is playing along" />}
                        </p>
                        {p.streak >= 2 && (
                          <p className="text-[10px] text-amber-300 inline-flex items-center gap-0.5">
                            <Flame className="h-3 w-3" /> {p.streak}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {p.has_answered && status === "running" && (
                        <span className="rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                          <Zap className="h-2.5 w-2.5 inline" /> Answered
                        </span>
                      )}
                      <span className="font-mono text-lg sm:text-xl font-black text-amber-300 tabular-nums">
                        {p.score.toLocaleString()}
                      </span>
                    </div>
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
          )}
        </section>

        {status === "final" && (
          <div className="mt-6 text-center">
            <p className="text-sm text-white/60">The game has ended. See the podium on the host screen for final standings.</p>
          </div>
        )}

        {err && (
          <p className="mt-4 text-center text-xs text-red-300">{err}</p>
        )}

        <div className="mt-10 text-center">
          <Link to="/" className="inline-flex items-center gap-1 text-xs text-white/40 hover:text-white/70 transition">
            Powered by Lectura <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}

const StatusPill = ({ status, q }) => {
  const map = {
    waiting: { text: "Waiting to start", color: "bg-slate-500/20 border-slate-400/40 text-slate-200" },
    running: { text: `Question ${q?.index != null ? q.index + 1 : "?"} live`, color: "bg-amber-400/20 border-amber-400/40 text-amber-200" },
    reveal: { text: "Reveal", color: "bg-emerald-500/20 border-emerald-400/40 text-emerald-200" },
    final: { text: "Game over", color: "bg-purple-500/20 border-purple-400/40 text-purple-200" },
  };
  const s = map[status] || map.waiting;
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wider ${s.color}`}>
      {s.text}
    </span>
  );
};
